'use client'

import { useMemo, useEffect, useRef } from 'react'
import { startOfWeek, format, differenceInDays, addDays } from 'date-fns'
import { useDocumentView } from '@/document'
import { useCreateJournal } from '@/entries'
import type { Entry } from '@/document'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WeeklyReflectionResult {
  reflectionEntry: Entry | null
  isGenerating: boolean
}

// ---------------------------------------------------------------------------
// Settings helpers
// ---------------------------------------------------------------------------

function readSetting(key: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback
  try {
    return localStorage.getItem(key) ?? fallback
  } catch {
    return fallback
  }
}

function isReflectionEnabled(): boolean {
  return readSetting('weekly-reflection-enabled', 'true') === 'true'
}

function getReflectionDay(): number {
  const raw = readSetting('weekly-reflection-day', '1')
  const n = parseInt(raw, 10)
  return n >= 0 && n <= 6 ? n : 1
}

// ---------------------------------------------------------------------------
// Narrative generation
// ---------------------------------------------------------------------------

interface NarrativeData {
  tasksCompleted: number
  topProjects: string[]
  journalCount: number
  mediaEntries: Array<{ title: string; type: string }>
  frequentPeople: Array<{ name: string; count: number }>
  staleTasks: Array<{ title: string }>
  staleCount: number
}

function gatherWeekData(
  entries: Record<string, Entry>,
  projects: Record<string, { name: string }>,
  weekStart: Date,
  weekEnd: Date,
): NarrativeData {
  let tasksCompleted = 0
  const projectCounts: Record<string, number> = {}
  let journalCount = 0
  const mediaEntries: Array<{ title: string; type: string }> = []
  const peopleMentions: Record<string, { name: string; count: number }> = {}
  const staleTaskList: Array<{ title: string }> = []
  const now = new Date()
  const STALE_DAYS = 14

  for (const entry of Object.values(entries)) {
    if (entry.archivedAt) continue

    const createdAt = new Date(entry.createdAt)
    const isThisWeek = createdAt >= weekStart && createdAt < weekEnd

    // Task completion
    if (entry.type === 'task') {
      const meta = entry.metadata as { status?: string; completedAt?: string }
      if (meta.status === 'done' && meta.completedAt) {
        const completedAt = new Date(meta.completedAt)
        if (completedAt >= weekStart && completedAt < weekEnd) {
          tasksCompleted++
          if (entry.projectId && projects[entry.projectId]) {
            const projName = projects[entry.projectId].name
            projectCounts[projName] = (projectCounts[projName] ?? 0) + 1
          }
        }
      }

      // Stale tasks
      if (meta.status !== 'done') {
        const daysSinceUpdate = differenceInDays(now, new Date(entry.updatedAt))
        if (daysSinceUpdate >= STALE_DAYS) {
          staleTaskList.push({ title: entry.title })
        }
      }
    }

    // Journal entries this week (exclude weekly reflections)
    if (
      entry.type === 'journal' &&
      isThisWeek &&
      !(entry.metadata as { isWeeklyReflection?: boolean }).isWeeklyReflection
    ) {
      journalCount++
    }

    // Media entries this week
    const mediaTypes = ['book', 'movie']
    if (mediaTypes.includes(entry.type) && isThisWeek) {
      const typeLabel = entry.type === 'book' ? 'reading list' : 'watchlist'
      mediaEntries.push({ title: entry.title, type: typeLabel })
    }

    // People mentions this week
    if (isThisWeek) {
      const meta = entry.metadata as {
        people?: Array<{ id: string; name: string }>
      }
      if (meta.people && Array.isArray(meta.people)) {
        for (const person of meta.people) {
          if (!peopleMentions[person.id]) {
            peopleMentions[person.id] = { name: person.name, count: 0 }
          }
          peopleMentions[person.id].count++
        }
      }
    }
  }

  // Top 2 projects
  const topProjects = Object.entries(projectCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([name]) => name)

  // People with 3+ mentions
  const frequentPeople = Object.values(peopleMentions)
    .filter((p) => p.count >= 3)
    .sort((a, b) => b.count - a.count)

  // Sort stale tasks by title, cap at 3
  const staleSorted = staleTaskList.slice(0, 3)

  return {
    tasksCompleted,
    topProjects,
    journalCount,
    mediaEntries,
    frequentPeople,
    staleTasks: staleSorted,
    staleCount: staleTaskList.length,
  }
}

function composeNarrative(data: NarrativeData): string {
  const sections: string[] = []

  // Quiet week check
  if (
    data.tasksCompleted === 0 &&
    data.journalCount === 0 &&
    data.mediaEntries.length === 0
  ) {
    sections.push(
      "This week was quiet. Nothing to summarize -- and that's fine.",
    )
    sections.push('---')
    sections.push('How are you feeling about this week?')
    return sections.join('\n\n')
  }

  // Task summary
  if (data.tasksCompleted > 0) {
    let taskLine = `You completed ${data.tasksCompleted} ${data.tasksCompleted === 1 ? 'task' : 'tasks'}`
    if (data.topProjects.length > 0) {
      taskLine += ` across ${data.topProjects.join(' and ')}`
    }
    taskLine += '.'
    sections.push(taskLine)
  }

  // Entry summary
  const entryParts: string[] = []
  if (data.journalCount > 0) {
    entryParts.push(
      `You wrote ${data.journalCount} journal ${data.journalCount === 1 ? 'entry' : 'entries'}`,
    )
  }
  if (data.mediaEntries.length > 0) {
    const first = data.mediaEntries[0]
    entryParts.push(`added "${first.title}" to your ${first.type}`)
  }
  if (entryParts.length > 0) {
    sections.push(entryParts.join(' and ') + '.')
  }

  // People patterns
  for (const person of data.frequentPeople) {
    sections.push(
      `${person.name} came up in ${person.count} entries this week.`,
    )
  }

  // Stale items
  if (data.staleCount > 0) {
    const titles = data.staleTasks.map((t) => `- ${t.title}`).join('\n')
    let staleLine = `${data.staleCount} ${data.staleCount === 1 ? 'task' : 'tasks'} from previous weeks ${data.staleCount === 1 ? 'is' : 'are'} still open:\n${titles}`
    if (data.staleCount > 3) {
      staleLine += `\n- ...and ${data.staleCount - 3} more`
    }
    sections.push(staleLine)
  }

  // Feeling prompt (always)
  sections.push('---')
  sections.push('How are you feeling about this week?')

  return sections.join('\n\n')
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Check if today is on or after the configured reflection day within the week. */
function shouldGenerateToday(): boolean {
  const configuredDay = getReflectionDay()
  const todayDay = new Date().getDay()
  // Sunday (0) is end-of-week when weekStartsOn=1
  // Convert to Monday-based index: Mon=0, Tue=1, ..., Sun=6
  const toMondayBased = (d: number) => (d === 0 ? 6 : d - 1)
  return toMondayBased(todayDay) >= toMondayBased(configuredDay)
}

/**
 * Detects whether a weekly reflection exists for the current week.
 * Generates one if not found and settings allow it.
 */
export function useWeeklyReflection(): WeeklyReflectionResult {
  const doc = useDocumentView()
  const createJournal = useCreateJournal()
  const generatedRef = useRef(false)

  // Find existing reflection for this week
  const existing = useMemo(() => {
    if (!doc) return null
    if (!isReflectionEnabled()) return null

    const now = new Date()
    const weekStart = startOfWeek(now, { weekStartsOn: 1 })
    const weekEnd = addDays(weekStart, 7)

    return (
      Object.values(doc.entries).find((entry) => {
        if (entry.type !== 'journal') return false
        const meta = entry.metadata as { isWeeklyReflection?: boolean }
        if (!meta.isWeeklyReflection) return false
        const createdAt = new Date(entry.createdAt)
        return createdAt >= weekStart && createdAt < weekEnd
      }) ?? null
    )
  }, [doc])

  // Generate reflection via effect (side-effect, not during render)
  useEffect(() => {
    if (!doc) return
    if (!isReflectionEnabled()) return
    if (existing) return
    if (!shouldGenerateToday()) return
    if (generatedRef.current) return

    generatedRef.current = true

    const now = new Date()
    const weekStart = startOfWeek(now, { weekStartsOn: 1 })
    const weekEnd = addDays(weekStart, 7)

    const data = gatherWeekData(doc.entries, doc.projects, weekStart, weekEnd)

    // Through the journal slice's write spec, not the document operations: a weekly reflection
    // is a journal entry, and `isWeeklyReflection` is a member of `JournalDraft` since #406.
    createJournal.mutate({
      title: `Week of ${format(weekStart, 'MMM d')}`,
      content: composeNarrative(data),
      projectId: null,
      topicIds: [],
      peopleIds: [],
      isWeeklyReflection: true,
    })
  }, [doc, createJournal, existing])

  return {
    reflectionEntry: existing,
    isGenerating: false,
  }
}
